import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Select, Spin, Table, Tag } from "antd";

import axios from "../../utils/axios";
import "../Logs/MapVues.css"; // uniquement pour la bulle flottante partagee (map-bulle)
import PageEntete, { useUrlParams, useUrlTab } from "../utils/PageEntete";
import "./Cartographie.css";
import VueArbre from "./VueArbre";
import VueGlacon from "./VueGlacon";
import { InfosEnv, LegendeStatuts, StatutBadge } from "./StatutsCarto";
import {
  construireLignes,
  decrireStatut,
  filtrerArbre,
  formaterDateCourte,
  formaterDateHeure,
  libelleClient,
  libelleDepuis,
  libelleServeur,
  trierClients,
  prestatairesDistincts,
  proprietesPagination,
} from "./cartographieDonnees";

const VUES = [
  { key: "tree", label: "Arbre" },
  { key: "icicle", label: "Vue d'ensemble" },
  { key: "table", label: "Tableau" },
];

const liste = (v) => (Array.isArray(v) ? v : []);
const optionFiltre = (input, option) =>
  String((option && option.label) || "").toLowerCase().includes(input.toLowerCase());

function Compteurs({ ligne }) {
  return (
    <span className="carto-nb">
      {ligne.nbEnv} env.
      {ligne.nbSans > 0 && (
        <Tag color="red" className="carto-warn">
          {ligne.nbSans} ⚠
        </Tag>
      )}
      {ligne.nbEcarts > 0 && (
        <Tag color="orange" className="carto-warn">
          {ligne.nbEcarts} écarts
        </Tag>
      )}
    </span>
  );
}

function CelluleNom({ ligne }) {
  if (ligne.type === "client" || ligne.type === "server") {
    return (
      <span>
        <strong>{ligne.nom}</strong>
        {ligne.detail ? <span className="carto-nb">{ligne.detail}</span> : null}
        <Compteurs ligne={ligne} />
      </span>
    );
  }
  if (ligne.type === "env") {
    const e = ligne.env;
    return (
      <span>
        <strong>{e.code}</strong> {e.label ? `— ${e.label}` : ""}
        {e.cancelled && <Tag className="carto-warn">annulé</Tag>}
        <InfosEnv env={e} />
      </span>
    );
  }
  const m = ligne.mod;
  return <span>{m.label || m.code || m.name}</span>;
}

const moduleSeulement = (render) => (_, ligne) => (ligne.type === "module" ? render(ligne.mod) : null);

const COLONNES = [
  { title: "Client / Serveur / Environnement / Module", key: "nom", render: (_, l) => <CelluleNom ligne={l} /> },
  {
    title: "Déclaré",
    key: "declare",
    render: moduleSeulement((m) => (
      <>
        {m.current_source && <span className="carto-pill actuelle">{m.current_source.value}</span>}
        {liste(m.previous_sources).map((s) => (
          <span key={s.key} className="carto-pill anterieure">
            {s.value}
          </span>
        ))}
      </>
    )),
  },
  {
    title: "Observé (30 j)",
    key: "observe",
    render: moduleSeulement((m) =>
      liste(m.observed_sources).map((o) => (
        <div key={o.provider} title={liste(o.jobs).join("\n")}>
          {o.provider}
          {o.last_run_at ? ` (${formaterDateHeure(o.last_run_at, "DD/MM HH:mm")})` : ""}
        </div>
      ))
    ),
  },
  {
    title: "Statut",
    key: "statut",
    render: moduleSeulement((m) => <StatutBadge module={m} />),
  },
  {
    title: "Depuis",
    key: "depuis",
    render: moduleSeulement((m) =>
      liste(m.since).map((s) => (
        <div key={s.key}>
          {libelleDepuis(s.key)} : {formaterDateCourte(s.value)}
        </div>
      ))
    ),
  },
  {
    title: "ASPI / Sync / SIREN",
    key: "statuts",
    render: moduleSeulement((m) =>
      liste(m.statuses).map((s) => {
        const d = decrireStatut(s);
        return d.tag ? (
          <Tag key={s.key} color={d.actif ? "green" : "default"}>
            {d.label} : {d.valeur}
          </Tag>
        ) : (
          <Tag key={s.key}>
            {d.label} : {d.valeur}
          </Tag>
        );
      })
    ),
  },
];

function Puce({ actif, onClick, children, testId }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={actif}
      className={`cti-chip${actif ? " on" : ""}`}
      onClick={onClick}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

// Supervision > Orchestration : client > serveur > environnement > module, sources de donnees.
function Cartographie() {
  const [clients, setClients] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [erreur, setErreur] = useState(false);
  const [annules, setAnnules] = useState(false);
  const [page, setPage] = useState({ current: 1, pageSize: 50 });
  // Vue (?view=) et client(s) (?client=1,2) lus et ecrits dans l'URL : lien partageable.
  const [vue, setVue] = useUrlTab(VUES.map((v) => v.key), "view");
  const [params, majUrl] = useUrlParams();
  const majUrlRef = useRef(majUrl);
  majUrlRef.current = majUrl;
  const clientParam = params.client || "";
  const idsClients = useMemo(
    () =>
      clientParam
        .split(",")
        .map((x) => parseInt(x, 10))
        .filter((x) => !isNaN(x)),
    [clientParam]
  );
  const [autres, setAutres] = useState({ serveurs: [], prestataires: [], ecarts: false });
  const filtres = useMemo(() => ({ ...autres, clients: idsClients }), [autres, idsClients]);
  // Vue Arbre : un seul client ; on garde le premier s'il y en a plusieurs.
  useEffect(() => {
    if (vue === "tree" && idsClients.length > 1) {
      majUrlRef.current({ client: idsClients[0] });
    }
  }, [vue, idsClients]);

  useEffect(() => {
    let actif = true;
    setLoaded(false);
    setErreur(false);
    axios
      .get("/api/supervision/cartography", annules ? { params: { include_cancelled: 1 } } : undefined)
      .then((res) => {
        if (!actif) return;
        setClients(trierClients(res && res.data && res.data.clients));
        setLoaded(true);
      })
      .catch(() => {
        if (!actif) return;
        setClients([]);
        setErreur(true);
        setLoaded(true);
      });
    return () => {
      actif = false;
    };
  }, [annules]);

  const optClients = useMemo(
    () => clients.map((c) => ({ value: c.id, label: libelleClient(c) })),
    [clients]
  );
  const optServeurs = useMemo(() => {
    const vus = new Map();
    clients.forEach((c) => liste(c.servers).forEach((s) => vus.set(s.id, libelleServeur(s))));
    const inconnu = (l) => l.startsWith("Serveur inconnu");
    return [...vus.entries()]
      .sort(([, a], [, b]) => Number(inconnu(a)) - Number(inconnu(b)) || a.localeCompare(b, "fr"))
      .map(([value, label]) => ({ value, label }));
  }, [clients]);
  const optPrestataires = useMemo(
    () => prestatairesDistincts(clients).map((p) => ({ value: p, label: p })),
    [clients]
  );

  const filtres_ = useMemo(() => filtrerArbre(clients, filtres), [clients, filtres]);
  // Vue Arbre : client affiche = 1er client de la barre present, sinon 1er de la liste filtree.
  const clientArbre = useMemo(() => {
    if (vue !== "tree") return null;
    const liste_ = filtrerArbre(clients, { ...filtres, clients: [] });
    return liste_.find((c) => c.id === filtres.clients[0]) || liste_[0] || null;
  }, [vue, clients, filtres]);
  // retour page 1 quand les filtres, la liste ou les donnees changent
  useEffect(() => {
    setPage((p) => ({ ...p, current: 1 }));
  }, [filtres, annules, clients]);
  const lignes = useMemo(() => construireLignes(filtres_), [filtres_]);
  const maj = (patch) => {
    const { clients: c, ...reste } = patch;
    if (c !== undefined) majUrl({ client: c.length ? c.join(",") : null });
    if (Object.keys(reste).length) setAutres((a) => ({ ...a, ...reste }));
  };

  let contenu;
  if (!loaded) {
    contenu = (
      <div className="carto-vide">
        <Spin />
      </div>
    );
  } else if (erreur) {
    contenu = (
      <Alert
        type="error"
        showIcon
        message="Impossible de charger la cartographie"
        description="Une erreur est survenue lors de la récupération des données. Réessayez plus tard."
      />
    );
  } else if (vue === "tree" ? !clientArbre : lignes.length === 0) {
    contenu = <div className="carto-vide">Aucun résultat pour ces filtres</div>;
  } else if (vue === "tree") {
    contenu = <VueArbre client={clientArbre} />;
  } else if (vue === "icicle") {
    contenu = <VueGlacon clients={filtres_} />;
  } else {
    contenu = (
      <Table
        size="small"
        columns={COLONNES}
        dataSource={lignes}
        pagination={proprietesPagination({ page, total: lignes.length, unite: "clients", onChange: setPage })}
        expandable={{ indentSize: 20 }}
      />
    );
  }

  return (
    <div className="cti-page carto-page">
      <PageEntete
        title="Supervision — Orchestration"
        items={VUES}
        value={vue}
        onChange={setVue}
        label="Vue"
      />
      <div className="cti-bar carto-filtres" role="group" aria-label="Filtres">
        <Select
          key={vue === "tree" ? "client-simple" : "client-multiple"}
          mode={vue === "tree" ? undefined : "multiple"}
          allowClear
          showSearch
          maxTagCount="responsive"
          placeholder="Client"
          aria-label="Client"
          options={optClients}
          value={vue === "tree" ? (clientArbre ? clientArbre.id : undefined) : filtres.clients}
          onChange={(v) =>
            maj({ clients: vue === "tree" ? (v === undefined || v === null ? [] : [v]) : v })
          }
          filterOption={optionFiltre}
        />
        <Select
          mode="multiple"
          allowClear
          showSearch
          maxTagCount="responsive"
          placeholder="Serveur"
          aria-label="Serveur"
          options={optServeurs}
          value={filtres.serveurs}
          onChange={(v) => maj({ serveurs: v })}
          filterOption={optionFiltre}
        />
        <Select
          mode="multiple"
          allowClear
          showSearch
          maxTagCount="responsive"
          placeholder="Prestataire"
          aria-label="Prestataire"
          options={optPrestataires}
          value={filtres.prestataires}
          onChange={(v) => maj({ prestataires: v })}
          filterOption={optionFiltre}
        />
        <span className="cti-sep" />
        <Puce
          actif={filtres.ecarts}
          onClick={() => maj({ ecarts: !filtres.ecarts })}
          testId="filtre-ecarts"
        >
          Écarts
        </Puce>
        <Puce actif={annules} onClick={() => setAnnules(!annules)} testId="filtre-annules">
          Afficher les environnements annulés
        </Puce>
      </div>
      <div className="cti-panel">{contenu}</div>
      {vue !== "icicle" && <LegendeStatuts />}
    </div>
  );
}

export default Cartographie;
